import { useMemo } from 'react'
import { TaskListItem } from '@myapp/contract'
import { useTimelineTasks } from '../../../entities/task'
import { isMidnightScheduledDate } from '../../../shared/lib/timeline/dateHelpers'

export { isMidnightScheduledDate }

export interface TimelineTasksForTechnician {
  timedTasks: TaskListItem[]
  dayLevelTasks: TaskListItem[]
}

export function useTimelineTasksByTechnician(from: string, to: string): {
  getTasksForTechnician: (technicianId: string) => TimelineTasksForTechnician
  timedTasksByTechnician: Map<string, TaskListItem[]>
} {
  const tasks = useTimelineTasks(from, to)

  const { byTechnician, timedTasksByTechnician } = useMemo(() => {
    const byTech = new Map<string, TimelineTasksForTechnician>()
    const timedByTech = new Map<string, TaskListItem[]>()

    for (const task of tasks) {
      if (!task.assignedTechnician || !task.plannedStart) continue
      if (task.needsRescheduling) continue
      const id = String(task.assignedTechnician.id)

      if (!byTech.has(id)) byTech.set(id, { timedTasks: [], dayLevelTasks: [] })
      if (!timedByTech.has(id)) timedByTech.set(id, [])

      const entry = byTech.get(id)!
      const effectiveStart = (task.status === 'COMPLETED' && task.actualStart != null)
        ? task.actualStart
        : task.plannedStart

      if (isMidnightScheduledDate(effectiveStart)) {
        entry.dayLevelTasks.push(task)
      } else {
        entry.timedTasks.push(task)
        timedByTech.get(id)!.push(task)
      }
    }

    return { byTechnician: byTech, timedTasksByTechnician: timedByTech }
  }, [tasks])

  const getTasksForTechnician = (technicianId: string): TimelineTasksForTechnician =>
    byTechnician.get(technicianId) ?? { timedTasks: [], dayLevelTasks: [] }

  return { getTasksForTechnician, timedTasksByTechnician }
}
